import { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useSanityGqlEndpoint } from 'state/network';
import logger from 'utils/logger';

import { EngineOfferListItem, EnginePersonalizedOffer, isEnginePersonalizedOffer } from '../types';

import {
  IPersonalizedData,
  fetchPersonalizedOfferData as fetchData,
} from './utils/personalized-offers';

export const usePersonalizedOffers: () => [
  (offers: readonly EngineOfferListItem[]) => Promise<IPersonalizedData | null>,
  { loading: boolean }
] = () => {
  const [loading, setLoading] = useState(false);

  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();
  const personalizedOffersTemplateEnabled = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_PERSONALIZED_OFFERS_TEMPLATE)
  );

  const fetchPersonalizedOfferData = useCallback(
    async (offers: EngineOfferListItem[]) => {
      let data = null;

      if (!personalizedOffersTemplateEnabled) {
        return data;
      }

      const configOffersIds = offers
        .filter(
          (offer): offer is EnginePersonalizedOffer =>
            isEnginePersonalizedOffer(offer) && !!offer.configId
        )
        .map(offer => offer.configId);

      if (!configOffersIds.length) {
        return data;
      }

      try {
        setLoading(true);
        data = await fetchData({ client, uri, configOffersIds });
      } catch (e) {
        logger.error({ error: e, message: 'Error fetching personalized offers data' });
      } finally {
        setLoading(false);
      }

      return data;
    },
    [personalizedOffersTemplateEnabled, client, uri]
  );

  return [fetchPersonalizedOfferData, { loading }];
};
